'use client';

import React, { useState } from 'react';
import { Heart, MessageCircle, Repeat2, Share, ThumbsUp, Bookmark, ImagePlus } from 'lucide-react';
import type { EditorPost } from './ContentEditorLayout';
import { useVisualLab } from '@/context/VisualLabContext';

interface Props {
  post: EditorPost;
}

export default function PostPreview({ post }: Props) {
  const [activeTab, setActiveTab] = useState<string>(post.platform);
  const { selectedImage, clearSelectedImage } = useVisualLab();

  const tabs = [post.platform];
  if (post.platform !== 'LI') tabs.push('LI');

  /* ── Shared Visual Lab image block ── */
  const VisualLabImageBlock = ({ aspectClass }: { aspectClass?: string }) => {
    if (!selectedImage) {
      return (
        <div className={`w-full ${aspectClass ?? 'aspect-square'} bg-gradient-to-br from-gray-100 to-gray-200 flex flex-col items-center justify-center gap-1`}>
          <ImagePlus size={18} className="text-gray-300" />
          <p className="text-xs text-gray-400">Bild aus Visual Lab</p>
        </div>
      );
    }
    return (
      <div
        className={`w-full ${aspectClass ?? 'aspect-square'} bg-gradient-to-br ${selectedImage.gradient} flex items-center justify-center relative`}
      >
        <div className="text-center">
          <div
            className="w-14 h-14 rounded mx-auto mb-2"
            style={{ background: selectedImage.color, boxShadow: `0 0 30px ${selectedImage.color}60` }}
          />
          <p className="text-xs text-white/50 mono px-2 leading-snug">
            {selectedImage.style} · {selectedImage.ratio}
          </p>
        </div>
        {/* Visual Lab badge */}
        <div className="absolute bottom-2 right-2 flex items-center gap-1 bg-black/70 text-white text-xs mono px-1.5 py-0.5 rounded">
          <ImagePlus size={9} />
          Visual Lab
        </div>
      </div>
    );
  };

  const renderLinkedInPreview = () => (
    <div className="bg-white rounded-lg overflow-hidden text-black max-w-sm mx-auto shadow-lg">
      {/* Header */}
      <div className="p-4">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0 text-sm font-700 text-gray-600">
            KB
          </div>
          <div>
            <p className="text-sm font-700 text-gray-900">Klaus Berger</p>
            <p className="text-xs text-gray-500">CEO · Berger GmbH · 1. Std.</p>
            <p className="text-xs text-gray-400">🌐</p>
          </div>
        </div>
        <div className="mt-3 text-sm text-gray-800 leading-relaxed whitespace-pre-line line-clamp-6">
          {post.content}
        </div>
        <button className="text-xs text-blue-600 mt-1">…mehr anzeigen</button>
      </div>
      {/* Image (if Visual Lab image is selected) */}
      {selectedImage && (
        <VisualLabImageBlock aspectClass="aspect-video" />
      )}
      {/* Footer */}
      <div className="px-4 pb-2 border-t border-gray-100">
        <div className="flex items-center justify-between py-2 text-xs text-gray-500">
          <span>👍 ❤️ 127</span>
          <span>34 Kommentare · 12 Reposts</span>
        </div>
        <div className="flex items-center justify-around pt-1">
          {[
            { icon: <ThumbsUp size={14} />, label: 'Gefällt mir' },
            { icon: <MessageCircle size={14} />, label: 'Kommentieren' },
            { icon: <Repeat2 size={14} />, label: 'Reposten' },
            { icon: <Share size={14} />, label: 'Senden' },
          ].map((action) => (
            <button
              key={`li-action-${action.label}`}
              className="flex items-center gap-1 text-xs text-gray-600 hover:text-blue-600 py-1.5 px-2 rounded hover:bg-gray-50 transition-colors"
            >
              {action.icon}
              <span className="hidden sm:inline">{action.label}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );

  const renderInstagramPreview = () => (
    <div className="bg-white rounded-lg overflow-hidden text-black max-w-xs mx-auto shadow-lg">
      {/* Header */}
      <div className="flex items-center gap-3 p-3">
        <div className="w-8 h-8 rounded-full bg-gradient-to-tr from-yellow-400 via-pink-500 to-purple-600 p-0.5">
          <div className="w-full h-full rounded-full bg-white flex items-center justify-center text-xs font-700 text-gray-700">
            B
          </div>
        </div>
        <div>
          <p className="text-xs font-700 text-gray-900">berger_gmbh</p>
          <p className="text-xs text-gray-400">Gesponsert</p>
        </div>
        <span className="ml-auto text-gray-400 text-lg">···</span>
      </div>
      {/* Visual Lab image */}
      <VisualLabImageBlock aspectClass="aspect-square" />
      {/* Actions */}
      <div className="p-3">
        <div className="flex items-center gap-4 mb-2">
          <Heart size={20} className="text-gray-700" />
          <MessageCircle size={20} className="text-gray-700" />
          <Share size={20} className="text-gray-700" />
          <Bookmark size={20} className="text-gray-700 ml-auto" />
        </div>
        <p className="text-xs font-700 text-gray-900 mb-1">284 Gefällt mir</p>
        <div className="text-xs text-gray-800 leading-relaxed">
          <span className="font-700">berger_gmbh</span>{' '}
          <span className="line-clamp-3">{post.content.replace(/\[.*?\]/g, '').trim()}</span>
        </div>
        <p className="text-xs text-gray-400 mt-1">Alle 47 Kommentare anzeigen</p>
        <p className="text-xs text-gray-400 uppercase tracking-wide mt-1" style={{ fontSize: '10px' }}>Vor 2 Stunden</p>
      </div>
    </div>
  );

  const renderXPreview = () => (
    <div className="bg-black border border-gray-800 rounded-xl overflow-hidden max-w-sm mx-auto">
      <div className="p-4">
        <div className="flex gap-3">
          <div className="w-10 h-10 rounded-full bg-gray-700 flex items-center justify-center flex-shrink-0 text-sm font-700 text-white">
            KB
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1 mb-1">
              <span className="text-sm font-700 text-white">Klaus Berger</span>
              <span className="text-xs text-gray-500">@Klaus_Berger_GmbH · 2h</span>
            </div>
            <div className="text-sm text-gray-100 leading-relaxed whitespace-pre-line">
              {post.content.slice(0, 280)}
              {post.content.length > 280 && (
                <span className="text-blue-400"> …mehr</span>
              )}
            </div>
            {/* Image in tweet if Visual Lab image selected */}
            {selectedImage && (
              <div className="mt-3 rounded-xl overflow-hidden">
                <VisualLabImageBlock aspectClass="aspect-video" />
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center justify-between mt-3 text-gray-500 text-xs">
          <div className="flex items-center gap-1 hover:text-blue-400 cursor-pointer">
            <MessageCircle size={14} />
            <span>23</span>
          </div>
          <div className="flex items-center gap-1 hover:text-green-400 cursor-pointer">
            <Repeat2 size={14} />
            <span>87</span>
          </div>
          <div className="flex items-center gap-1 hover:text-red-400 cursor-pointer">
            <Heart size={14} />
            <span>412</span>
          </div>
          <div className="flex items-center gap-1 hover:text-blue-400 cursor-pointer">
            <Share size={14} />
          </div>
          <div className="flex items-center gap-1 hover:text-blue-400 cursor-pointer">
            <Bookmark size={14} />
          </div>
        </div>
      </div>
    </div>
  );

  const renderFacebookPreview = () => (
    <div className="bg-white rounded-lg overflow-hidden text-black max-w-sm mx-auto shadow-lg">
      <div className="p-4">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center font-700 text-blue-700">
            B
          </div>
          <div>
            <p className="text-sm font-700 text-gray-900">Berger GmbH</p>
            <p className="text-xs text-gray-500">Jetzt · 🌐</p>
          </div>
          <span className="ml-auto text-gray-400">···</span>
        </div>
        <div className="text-sm text-gray-800 leading-relaxed whitespace-pre-line line-clamp-6">
          {post.content}
        </div>
      </div>
      {/* Visual Lab image */}
      <VisualLabImageBlock aspectClass="aspect-video" />
      <div className="px-4 pb-2 border-t border-gray-100">
        <div className="flex items-center justify-between py-2 text-xs text-gray-500">
          <span>👍 😂 ❤️ 89</span>
          <span>23 Kommentare</span>
        </div>
        <div className="flex items-center justify-around pt-1">
          {[
            { icon: <ThumbsUp size={14} />, label: 'Gefällt mir' },
            { icon: <MessageCircle size={14} />, label: 'Kommentieren' },
            { icon: <Share size={14} />, label: 'Teilen' },
          ].map((action) => (
            <button
              key={`fb-action-${action.label}`}
              className="flex items-center gap-1 text-xs text-gray-600 py-1.5 px-3 rounded hover:bg-gray-50 transition-colors"
            >
              {action.icon}
              {action.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );

  const previewMap: Record<string, React.ReactNode> = {
    LI: renderLinkedInPreview(),
    IG: renderInstagramPreview(),
    X: renderXPreview(),
    FB: renderFacebookPreview(),
  };

  return (
    <div className="w-80 flex-shrink-0 flex flex-col bg-black">
      {/* Header */}
      <div className="px-4 py-3 border-b border-border flex-shrink-0">
        <p className="section-label mb-2">Live-Vorschau</p>
        <div className="flex gap-1">
          {['LI', 'IG', 'X', 'FB'].map((plat) => (
            <button
              key={`preview-tab-${plat}`}
              onClick={() => setActiveTab(plat)}
              className={`text-xs mono px-2.5 py-1 rounded border transition-all ${
                activeTab === plat
                  ? 'border-accent text-accent bg-accent/8' :'border-border text-muted hover:border-accent/40'
              }`}
            >
              {plat}
            </button>
          ))}
        </div>
      </div>

      {/* Visual Lab image indicator */}
      {selectedImage ? (
        <div className="px-4 py-2 border-b border-border flex items-center justify-between bg-accent/5 flex-shrink-0">
          <div className="flex items-center gap-1.5">
            <div
              className="w-3 h-3 rounded-sm flex-shrink-0"
              style={{ background: selectedImage.color }}
            />
            <span className="text-xs mono text-accent truncate max-w-[140px]">
              {selectedImage.style} · {selectedImage.ratio}
            </span>
          </div>
          <button
            onClick={clearSelectedImage}
            className="text-xs text-muted hover:text-white transition-colors ml-2 flex-shrink-0"
            title="Bild entfernen"
          >
            ✕
          </button>
        </div>
      ) : (
        <div className="px-4 py-2 border-b border-border flex items-center gap-1.5 flex-shrink-0">
          <ImagePlus size={11} className="text-muted" />
          <span className="text-xs text-muted mono">Kein Visual Lab Bild verknüpft</span>
        </div>
      )}

      {/* Preview area */}
      <div className="flex-1 overflow-y-auto p-4 bg-surface-2/30">
        <div className="fade-in">
          {previewMap[activeTab] ?? (
            <div className="text-center py-10">
              <p className="text-xs text-muted">Vorschau nicht verfügbar für {activeTab}</p>
            </div>
          )}
        </div>
      </div>

      {/* Footer info */}
      <div className="px-4 py-3 border-t border-border flex-shrink-0">
        <p className="text-xs text-muted mono">
          Plattform-Limit: <span className="text-white">{post.chars}/{platformLimits[post.platform] ?? '∞'}</span> Zeichen
        </p>
        <p className="text-xs text-muted mt-1">
          Format: <span className="text-accent">{post.format}</span>
        </p>
      </div>
    </div>
  );
}

const platformLimits: Record<string, number> = {
  LI: 3000,
  IG: 2200,
  X: 280,
  FB: 63206,
  BLOG: 99999,
};